import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Plus, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { NewsTicker, PRIORITIES } from './NewsTicker';
import { AnnouncementLinkFields, EMPTY_LINK } from './AnnouncementLinkFields';

const inDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const fmtDate = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

export const AnnouncementsManager = ({ open, onClose, onChanged }) => {
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState('');
  const [endsAt, setEndsAt] = useState(inDays(7));
  const [audience, setAudience] = useState('staff');
  const [priority, setPriority] = useState('info');
  const [link, setLink] = useState(EMPTY_LINK);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setItems((await api.get('/announcements')).data || []); } catch (e) { toast.error('Chargement impossible'); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const refresh = async () => { await load(); onChanged?.(); };
  const errMsg = (e) => e.response?.data?.detail || 'Erreur';

  const add = async () => {
    if (!message.trim()) { toast.error('Saisissez un message'); return; }
    setBusy(true);
    try {
      const portalTabs = audience === 'all' ? { tenant_tab: link.tenant_tab || null, owner_tab: link.owner_tab || null } : {};
      await api.post('/announcements', {
        message, ends_at: endsAt, audience, priority,
        link_type: link.link_type, link_target: link.link_target || null, link_label: link.link_label || null, ...portalTabs,
      });
      setMessage(''); setPriority('info'); setLink(EMPTY_LINK); toast.success('Message publié'); await refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = async (a) => {
    try { await api.put(`/announcements/${a.id}`, { is_active: !a.is_active }); await refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (a) => {
    if (!window.confirm('Supprimer ce message défilant ?')) return;
    try { await api.delete(`/announcements/${a.id}`); toast.success('Message supprimé'); await refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  const expired = (a) => new Date(a.ends_at) <= new Date();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg min-w-0 max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="announcements-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Messages défilants</DialogTitle>
          <DialogDescription>Affichés en haut du tableau de bord jusqu'à leur date de fin.</DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-3 rounded-xl bg-[#F5EFE1]/60 p-3">
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)} maxLength={300} rows={2}
            placeholder="Ex : Réunion d'équipe vendredi à 9h"
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-[#B85C38] outline-none bg-white"
            data-testid="announcement-message-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-semibold text-gray-600">Jusqu'au
              <input type="date" value={endsAt} min={inDays(0)} onChange={(e) => setEndsAt(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white" data-testid="announcement-end-input" />
            </label>
            <label className="text-xs font-semibold text-gray-600">Visible par
              <select value={audience} onChange={(e) => setAudience(e.target.value)}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white" data-testid="announcement-audience-select">
                <option value="staff">L'équipe seulement</option>
                <option value="all">Équipe + locataires + propriétaires</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="announcement-priority-group">
            {Object.entries(PRIORITIES).map(([key, p]) => {
              const { Icon } = p;
              const on = priority === key;
              return (
                <button key={key} type="button" onClick={() => setPriority(key)}
                  className={`flex items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-xs font-bold transition-transform ${on ? `${p.pill} scale-[1.03]` : 'bg-white border border-gray-200 text-gray-600 hover:border-[#B85C38]/40'}`}
                  data-testid={`announcement-priority-${key}`}>
                  <Icon className="w-3.5 h-3.5" strokeWidth={2.4} /> {p.label}
                </button>
              );
            })}
          </div>
          <AnnouncementLinkFields value={link} onChange={setLink} audience={audience} />
          {message.trim() && (
            <div className="min-w-0 overflow-hidden" data-testid="announcement-preview">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Aperçu</p>
              <NewsTicker items={[{ id: 'preview', message: message.trim(), priority, link: link.link_type !== 'none' && link.link_target ? { type: link.link_type, target: link.link_target, label: link.link_label } : null }]} onOpen={() => {}} />
            </div>
          )}
          <Button onClick={add} disabled={busy} className="w-full bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="announcement-add-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />} Publier
          </Button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-2" data-testid="announcements-list">
          {!items.length && <p className="text-sm text-gray-500 text-center py-4">Aucun message</p>}
          {items.map((a) => (
            <div key={a.id} className={`flex items-start gap-2 rounded-xl border p-3 ${a.is_active && !expired(a) ? 'border-gray-200' : 'border-dashed border-gray-200 opacity-60'}`} data-testid={`announcement-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase mb-1 ${(PRIORITIES[a.priority] || PRIORITIES.info).pill}`} data-testid={`announcement-priority-badge-${a.id}`}>
                  {(PRIORITIES[a.priority] || PRIORITIES.info).label}
                </span>
                <p className="text-sm text-[#2B3A2C] break-words">{a.message}</p>
                {a.link_type && a.link_type !== 'none' && (
                  <p className="text-[11px] text-[#B85C38] mt-0.5 truncate" data-testid={`announcement-link-info-${a.id}`}>
                    Lien : {a.link_type === 'property' ? `bien « ${a.link_title || ''} »` : a.link_target}{a.link_label ? ` · « ${a.link_label} »` : ''}
                  </p>
                )}
                {(a.tenant_tab || a.owner_tab) && (
                  <p className="text-[11px] text-[#2B3A2C]/70 mt-0.5" data-testid={`announcement-portal-links-${a.id}`}>
                    Portails : {[a.tenant_tab && `locataires → ${a.tenant_tab}`, a.owner_tab && `propriétaires → ${a.owner_tab}`].filter(Boolean).join(' · ')}
                  </p>
                )}
                <p className="text-[11px] text-gray-500 mt-1">
                  {expired(a) ? 'Expiré le ' : "Jusqu'au "}{fmtDate(a.ends_at)} · {a.audience === 'all' ? 'Équipe + portails' : 'Équipe'}{!a.is_active && ' · En pause'}
                </p>
              </div>
              <button type="button" onClick={() => toggle(a)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title={a.is_active ? 'Mettre en pause' : 'Réactiver'} data-testid={`announcement-toggle-${a.id}`}>
                {a.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button type="button" onClick={() => remove(a)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Supprimer" data-testid={`announcement-delete-${a.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementsManager;
